/**
 * 404 Not Found Page — PatrolPuffin Marketing Portal
 *
 * Friendly 404 page with helpful links to guide users
 * back to relevant content.
 *
 * @module portals/marketing/pages/NotFoundPage
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBrand } from '@puffin-core/brand-assets';
import { MarketingSeo, useAnalytics, webPage, breadcrumbList } from '@puffin-core/marketing-shell';
import { PATROL_MARKETING_ROUTES } from '../constants/patrolMarketingRoutePaths';

const APP_KEY = 'patrol';

/**
 * Quick navigation links for the 404 page
 * @constant {Array<Object>}
 */
const QUICK_LINKS = [
  { to: PATROL_MARKETING_ROUTES.HOME, labelKey: 'notFound.homeLink' },
  { to: PATROL_MARKETING_ROUTES.FEATURES, labelKey: 'notFound.featuresLink' },
  { to: PATROL_MARKETING_ROUTES.CONTACT, labelKey: 'notFound.contactLink' },
];

/**
 * Not Found Page Component
 *
 * @returns {React.JSX.Element} 404 page
 */
const NotFoundPage = () => {
  const { t } = useTranslation();
  const { trackPageView } = useAnalytics();
  const brand = getBrand(APP_KEY);

  useEffect(() => {
    trackPageView(window.location.pathname, '404 Not Found');
  }, [trackPageView]);

  const jsonLd = useMemo(
    () => [
      webPage({
        appKey: APP_KEY,
        brand,
        url: 'https://patrolpuffin.com/404',
        title: t('notFound.documentTitle'),
        description:
          'The page you are looking for could not be found. Return to the PatrolPuffin homepage.',
      }),
      breadcrumbList({
        crumbs: [
          { name: 'Home', url: 'https://patrolpuffin.com/' },
          { name: 'Not Found', url: 'https://patrolpuffin.com/404' },
        ],
      }),
    ],
    [brand, t],
  );

  const ogImageUrl = `https://patrolpuffin.com${brand.assets.ogCard}`;

  return (
    <>
      <MarketingSeo
        title={t('notFound.documentTitle')}
        description={t('notFound.seoDescription')}
        canonical="https://patrolpuffin.com/404"
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={jsonLd}
      />

      <div className="not-found-page">
        <div className="not-found-content">
          <h1 className="not-found-title">{t('notFound.title')}</h1>
          <p className="not-found-message">{t('notFound.message')}</p>

          <nav className="not-found-links" aria-label={t('notFound.helpfulLinksAriaLabel')}>
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="btn btn-marketing-lg btn-marketing-secondary"
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default NotFoundPage;
